import { SvgIconComponent } from "@mui/icons-material";
import { IconTypes } from "../../components/icons.types";
import { JourneyType, JourneySteps } from "./journey";
import { Permission } from "../accounts/account";
import { FeatureKey } from "./features";

export interface BaseStep<T extends JourneyType = JourneyType> {
  key: string;
  label?: string;
  description?: string;
  icon?: SvgIconComponent;
  journeyType?: T;
  completionStep?: JourneySteps<T>;
  permission?: Permission;
  completed?: boolean;
  feature?: FeatureKey;
}

export interface GuideStep extends BaseStep {
  tips?: string[];
  substeps: BaseStep[];
}

export const guideSteps: GuideStep[] = [
  {
    key: "products",
    label: "Add Products",
    icon: IconTypes.Product,

    journeyType: "onboarding",
    completionStep: "first_product",
    permission: "products",
    description: "Import or create your products",
    tips: [
      "Choose the fastest way to add your products - import or create manually",
      "You can always update products and add more later",
      "Start with a few products to learn the system",
    ],
    substeps: [
      {
        key: "import_ai",
        feature: "concierge",
        label: "AI Import",
        description: "Import with AI assistance",
      },
      {
        key: "import_shopify",
        feature: "shopify",
        description: "Connect your store",
      },
      {
        key: "import_csv",
        feature: "csv",
        label: "Upload CSV / Excel",
        description: "Import from spreadsheet",
      },
      {
        key: "product_upload",
        feature: "productUpload",
        label: "Product Bulk Upload",
        description: "Upload products with images",
      },
      {
        key: "add_manual",
        feature: "products",
        label: "Add Manually",
        description: "Create manually one by one",
      },
    ],
  },
  {
    key: "catalog",
    label: "Create Catalog",
    icon: IconTypes.Catalog,
    journeyType: "onboarding",
    completionStep: "first_catalog",
    permission: "catalogs",
    description: "Start your first product catalog",
    tips: [
      "Templates help you create professional catalogs quickly",
      "You can customize every aspect of your catalog later",
    ],
    substeps: [
      {
        key: "new_catalog",
        label: "Blank Catalog",
        description: "Start from scratch",
        feature: "catalogs",
      },
      {
        key: "import_pdf",
        label: "Import PDF",
        description: "Import from existing PDF",
        feature: "pdfImport",
      },
      {
        key: "catalogTemplates",
        label: "Use Template",
        description: "Start with a design",
        feature: "catalogTemplates",
      },
      {
        key: "open_catalog_designer",
        label: "Open Catalog Designer",
        description: "Edit catalog pages and content",
        feature: "openCatalogDesigner",
      },
    ],
  },

  {
    key: "quick_design",
    label: "Quick Catalog",
    icon: IconTypes.Awesome,
    permission: "catalogs",
    description: "Create a professional catalog in minutes",
    tips: [
      "<b>Auto Grid</b> instantly creates multi-product pages",
      "<b>Product Lists</b> are perfect for price lists and indexes",
      "These tools help you create catalogs 10x faster",
    ],
    substeps: [
      {
        key: "catalog_builder",
        feature: "catalogBuilder",
      },
      {
        key: "use_auto_grid",
        feature: "autoGrid",
      },
      {
        key: "use_product_list",
        feature: "productList",
      },
    ],
  },

  {
    key: "publish",
    label: "Share Catalog",
    icon: IconTypes.Share,
    permission: "catalogs",
    description: "Share your catalog with customers",
    tips: [
      "Preview ensures everything looks perfect",
      "Share online for interactive browsing",
      "Download PDF for printing or offline sharing",
    ],
    substeps: [
      {
        key: "preview",
        label: "Preview Online",
        description: "Review your catalog",
        icon: IconTypes.OpenInNew,
        feature: "catalog_preview",
        journeyType: "catalog_creation",
        completionStep: "preview",
      },
      {
        key: "publish",
        label: "Publish Catalog",
        description: "Make it public",
        icon: IconTypes.Publish,
        feature: "catalog_publish",
        journeyType: "onboarding",
        completionStep: "catalog_published",
      },
      {
        key: "download",
        label: "Download PDF",
        description: "Get a print-ready version",
        icon: IconTypes.PDF,
        feature: "catalog_download",
        journeyType: "catalog_creation",
        completionStep: "download_pdf",
      },
      {
        key: "share",
        label: "Share Online",
        description: "Share with customers",
        icon: IconTypes.Share,
        feature: "catalog_share",
        journeyType: "catalog_creation",
        completionStep: "share",
      },
    ],
  },
  {
    key: "product_setup",
    label: "Organize Products",
    icon: IconTypes.Category,
    permission: "products",
    description: "Structure your product data for efficient catalog creation",
    tips: [
      "Use <b>Categories</b> to define product properties (size, color, etc)",
      "Create <b>Collections</b> to group products for different catalogs",
      "Add <b>Custom fields</b> for unique product information",
      "Set up <b>Variants</b> for products with options",
      "Manage multiple product images efficiently",
    ],
    substeps: [
      {
        key: "categories",
        feature: "categories",
        description: "Add custom product fields and options",
      },
      {
        key: "collections",
        feature: "collections",
        description: "Organize your products",
      },
      {
        key: "variants",
        label: "Product Variants",
        description: "Manage product options",
        icon: IconTypes.Tune,
        journeyType: "product_setup",
        completionStep: "add_variants",
        feature: "products",
      },
      {
        key: "productCatalogMapping",
        feature: "productCatalogMapping",
        description: "View product-catalog relationships",
      },
    ],
  },
  {
    key: "showroom",
    label: "Product Showroom",
    icon: IconTypes.Store,
    journeyType: "showroom",
    completionStep: "showroom_sharing",
    permission: "catalogs",
    description: "Create a searchable product website",
    tips: [
      "Showroom automatically creates a searchable website from your products",
      "Customers can easily find products with search and filtering",
      "Low maintenance and highly customizable",
    ],
    substeps: [
      {
        key: "showroom",
        label: "Create Showroom",
        description: "Set up your first showroom",
        feature: "showroom",
        journeyType: "discovery",
        completionStep: "setup_showroom",
      },
      {
        key: "showroomDisplay",
        label: "Customize Display",
        description: "Personalize your showroom",
        feature: "showroomDisplay",
        journeyType: "discovery",
        completionStep: "customize_showroom",
      },
      {
        key: "showroomProducts",
        label: "Manage Products",
        description: "Select products to display",
        feature: "showroomProducts",
        journeyType: "discovery",
        completionStep: "showroom_products",
      },
      {
        key: "showroomPages",
        label: "Custom Pages",
        description: "Add information pages",
        feature: "showroomPages",
        journeyType: "discovery",
        completionStep: "showroom_pages",
      },
      {
        key: "showroomSharing",
        label: "Share Showroom",
        description: "Set privacy and sharing",
        feature: "showroomSharing",
        journeyType: "discovery",
        completionStep: "showroom_sharing",
      },
    ],
  },
  {
    key: "advanced_design",
    label: "Advanced Design",
    icon: IconTypes.Design,
    permission: "catalogs",
    description: "Create custom catalog designs",
    tips: [
      "Use <b>Product Cards</b> to create unique product displays",
      "Design <b>Page Layouts</b> to match your brand",
      "Save your designs to reuse in other catalogs",
    ],
    substeps: [
      {
        key: "card",
        label: "Use Product Card",
        description: "Apply a card to your page",
        feature: "card",
      },
      {
        key: "design_card",
        feature: "productCard",
      },
      {
        key: "layout",
        feature: "layout",
      },
      {
        key: "design_layout",
        label: "Design Layout",
        description: "Build your page layouts",
        icon: IconTypes.Layout,
        feature: "design_layout",
        journeyType: "catalog_creation",
        completionStep: "design_layout",
        permission: "catalogs",
      },
    ],
  },
  {
    key: "orders",
    label: "Order Management",
    icon: IconTypes.Order,
    journeyType: "order_management",
    completionStep: "setup_orders",
    permission: "products",
    description: "Manage customer orders and requests",
    tips: [
      "Set up custom order forms to collect exactly what you need",
      "Process payments securely with Stripe integration",
      "Track orders from submission to completion",
      "Export order data for analysis and fulfillment",
    ],
    substeps: [
      {
        key: "discover_orders",
        label: "View Orders",
        description: "See all customer orders",
        feature: "orders",
        journeyType: "order_management",
        completionStep: "discover_orders",
      },
      {
        key: "setup_orders",
        label: "Configure Settings",
        description: "Set up order forms and options",
        feature: "orderSettings",
        journeyType: "order_management",
        completionStep: "setup_orders",
      },
      {
        key: "create_first_order",
        label: "Create Order",
        description: "Add a new order manually",
        feature: "orderForm",
        journeyType: "order_management",
        completionStep: "create_first_order",
      },
      {
        key: "configure_payments",
        label: "Payment Setup",
        description: "Connect Stripe for payments",
        feature: "paymentSettings",
        journeyType: "order_management",
        completionStep: "configure_payments",
      },
      {
        key: "manage_order_status",
        label: "Process Orders",
        description: "Accept, reject, and close orders",
        feature: "orderStatus",
        journeyType: "order_management",
        completionStep: "manage_order_status",
      },
      {
        key: "export_orders",
        label: "Export Data",
        description: "Download orders as CSV",
        feature: "orderExport",
        journeyType: "order_management",
        completionStep: "export_orders",
      },
    ],
  },
  {
    key: "inventory",
    label: "Inventory Management",
    icon: IconTypes.Inventory,
    journeyType: "inventory_management",
    completionStep: "update_single_quantity",
    permission: "products",
    description: "Track and update product quantities",
    tips: [
      "View all product and variant quantities in one place",
      "Update quantities inline with instant saves",
      "Use filters to find specific products quickly",
      "Export inventory data for external analysis",
    ],
    substeps: [
      {
        key: "view_inventory_grid",
        label: "View Inventory",
        description: "See all product quantities",
        feature: "inventory",
        journeyType: "inventory_management",
        completionStep: "view_inventory_grid",
      },
      {
        key: "update_single_quantity",
        label: "Update Quantity",
        description: "Edit individual quantities",
        feature: "inventoryEdit",
        journeyType: "inventory_management",
        completionStep: "update_single_quantity",
      },
      {
        key: "bulk_quantity_update",
        label: "Bulk Updates",
        description: "Update multiple items at once",
        feature: "inventoryBulk",
        journeyType: "inventory_management",
        completionStep: "bulk_quantity_update",
      },
      {
        key: "use_inventory_filters",
        label: "Filter & Search",
        description: "Find products quickly",
        feature: "inventoryFilters",
        journeyType: "inventory_management",
        completionStep: "use_inventory_filters",
      },
      {
        key: "export_inventory",
        label: "Export Data",
        description: "Download inventory as CSV",
        feature: "inventoryExport",
        journeyType: "inventory_management",
        completionStep: "export_inventory",
      },
    ],
  },
  {
    key: "advanced",
    label: "Power Features",
    icon: IconTypes.Dashboard,
    description: "Unlock more catalog possibilities",
    tips: [
      "Centralize image management for products and catalogs",
      "Use parameters for dynamic catalog content",
      "Collaborate with your team efficiently",
    ],
    substeps: [
      {
        key: "manage_images",
        feature: "images",
      },
      {
        key: "use_parameters",
        label: "Use Parameters",
        description: "Add dynamic content",
        icon: IconTypes.Tune,
        feature: "parameters",
        journeyType: "discovery",
        completionStep: "use_parameters",
      },
      {
        key: "add_user",
        label: "Add User",
        description: "Invite team members",
        icon: IconTypes.Users,
        feature: "account_users",
        journeyType: "discovery",
        completionStep: "add_user",
        permission: "admin",
      },
      {
        key: "settings",
        label: "Account Settings",
        description: "Manage your account",
        icon: IconTypes.Settings,
        feature: "account",
      },
      {
        key: "support",
        label: "Get Support",
        description: "Contact our team",
        icon: IconTypes.Chat,
      },
    ],
  },

  {
    key: "automation",
    label: "Automate Workflows",
    icon: IconTypes.Connect,
    permission: "products", // Most users can access automation
    description: "Connect to APIs and external services",
    tips: [
      "Use our REST API for custom integrations",
      "Connect to 500+ apps with Zapier automations",
      "Automate product updates, notifications, and more",
    ],
    substeps: [
      {
        key: "automation_hub",
        feature: "automation",
        description: "Explore automation options",
        journeyType: "discovery",
        completionStep: "use_automation",
      },
    ],
  },
];
